<template>
  <Transition name="modal-fade">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center w-full h-screen"
      :class="{ 'bg-blackly/75': !fullscreen, 'bg-blackly': fullscreen, 'px-4': !video }"
      @mousedown.self="close"
    >
      <div
        ref="modalContainer"
        class="relative p-4 md:p-8"
        :class="[
          fullscreen ? 'max-w-screen-2xl w-full h-full flex items-center justify-center' : 'md:p-6 bg-white max-w-xl',
          !fullscreen && !disableScroll && 'overflow-y-scroll',
          minWidth ? 'max-h-[80vh] min-w-[300px] md:min-w-[650px]' : 'w-auto',
        ]"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <button
          v-if="!noCloseBtn"
          ref="closeButton"
          :aria-label="$t('common.close')"
          class="lg:w-16 lg:h-16 absolute top-0 right-0 z-20 flex items-center justify-center w-12 h-12 mx-4 my-2 fluid-3xl leading-tightest"
          :class="{ 'bg-blackly/75 text-white rounded-full': fullscreen }"
          @click.prevent="close"
          :title="$t('common.close')"
        >
          <CloseIcon class="h-6" />
        </button>
        <slot />
      </div>
    </div>
  </Transition>
</template>

<script setup>
import CloseIcon from '@/components/icons/CloseIcon';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

const props = defineProps({
  fullscreen: {
    type: Boolean,
    default: false,
  },
  video: {
    type: Boolean,
    default: false,
  },
  disableScroll: {
    type: Boolean,
    default: false,
  },
  minWidth: {
    type: Boolean,
    default: false,
  },
  noCloseBtn: {
    type: Boolean,
    default: false,
  },
  triggerElement: {
    type: [HTMLElement, null],
    default: null,
  },
});

const emit = defineEmits(['close']);

const modalContainer = ref(null);
const closeButton = ref(null);
const { $focusTrapManager } = useNuxtApp();

// Generate unique trap name for this modal instance
const trapName = `modal-${Math.random().toString(36).substr(2, 9)}`;

const close = () => {
  emit('close');
};

// Create focus trap using @vueuse/integrations
const { activate: activateVueFocusTrap, deactivate: deactivateVueFocusTrap } = useFocusTrap(modalContainer);

const handleKeyDown = (event) => {
  if (event.key === 'Escape') {
    close();
  }
};

onMounted(() => {
  nextTick(() => {
    // Use the passed trigger element or fallback to current active element
    const triggerElement = props.triggerElement || document.activeElement;

    if ($focusTrapManager) {
      // Create a wrapper for the vue focus trap to work with focus trap manager
      const focusTrapWrapper = {
        activate: () => {
          activateVueFocusTrap();
          // Focus the close button or first focusable element
          if (closeButton.value && !props.noCloseBtn) {
            closeButton.value.focus();
          }
        },
        deactivate: () => {
          deactivateVueFocusTrap();
        },
      };

      // Register with focus trap manager (priority 50 - lower than OneTrust but higher than other components)
      $focusTrapManager.register(trapName, focusTrapWrapper, 50, triggerElement);
      $focusTrapManager.activate(trapName);
    } else {
      // Fallback to regular vue focus trap if manager not available
      if (closeButton.value && !props.noCloseBtn) {
        closeButton.value.focus();
      }
      activateVueFocusTrap();
    }
  });

  window.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);

  if ($focusTrapManager) {
    $focusTrapManager.unregister(trapName);
  } else {
    deactivateVueFocusTrap();
  }
});
</script>
